import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Phone, ArrowRight, Eye, Target, Home, Utensils } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { mockData } from '../mock';

const AsianHornet = () => {
  React.useEffect(() => {
    document.title = "Frelon asiatique en Gironde : dangers & destruction de nid | Frelon33™";
  }, []);

  const handleCall = () => {
    window.location.href = 'tel:0676583038';
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-red-50 to-white py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="flex items-center space-x-2 text-red-600 font-medium">
                  <AlertTriangle className="h-5 w-5" />
                  <span>Espèce invasive dangereuse</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                  Le <span className="text-red-600">frelon asiatique</span><br />
                  en Gironde
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Tout ce que vous devez savoir sur cette espèce invasive et les mesures d'urgence à prendre.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={handleCall}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-4 text-lg rounded-lg transition-all duration-200 transform hover:scale-105 flex items-center justify-center space-x-2"
                >
                  <Phone className="h-5 w-5" />
                  <span>Intervention d'urgence</span>
                </Button>
                <Link to="/contact">
                  <Button
                    variant="outline"
                    className="border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white font-semibold px-8 py-4 text-lg rounded-lg transition-all duration-200 w-full sm:w-auto flex items-center justify-center space-x-2"
                  >
                    <span>Demander un devis</span>
                    <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_frelon33-experts/artifacts/ndjltdgo_FRELONSURFEUILLES.jpg"
                alt="Frelon asiatique sur feuilles" 
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-red-600 text-white p-4 rounded-xl shadow-lg">
                <div className="text-center">
                  <p className="font-bold text-2xl">2004</p>
                  <p className="text-sm">Première apparition en France</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Origin Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Origine et propagation
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Card className="border-l-4 border-red-600">
                <CardContent className="p-6">
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {mockData.asianHornetInfo.origin}
                  </p>
                </CardContent>
              </Card>
              
              <div className="bg-red-50 p-6 rounded-xl">
                <h3 className="text-xl font-semibold text-red-800 mb-4">Progression alarmante</h3>
                <p className="text-red-700">
                  En moins de 20 ans, le frelon asiatique a colonisé la quasi-totalité du territoire français, 
                  causant des dégâts considérables sur la biodiversité locale et l'apiculture.
                </p>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_frelon33-experts/artifacts/63eba81d_nid%20dans%20arbres.jpg"
                alt="Nid de frelon asiatique dans un arbre" 
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Dangers Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pourquoi est-ce si dangereux ?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {mockData.asianHornetInfo.dangers.map((danger, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-t-4 border-red-600">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-red-100 p-2 rounded-full flex-shrink-0">
                      <AlertTriangle className="h-6 w-6 text-red-600" />
                    </div>
                    <p className="text-gray-700 text-lg">{danger}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Educational Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Guide complet du frelon asiatique
            </h2>
            <p className="text-xl text-gray-600">
              Apprenez à identifier, comprendre et réagir face à cette espèce invasive
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="identification" className="border border-gray-200 rounded-lg overflow-hidden">
              <AccordionTrigger className="hover:no-underline px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex items-center space-x-3">
                  <Eye className="h-5 w-5 text-yellow-600" />
                  <span className="text-lg font-semibold">Comment l'identifier ?</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 py-4">
                <div className="space-y-4">
                  {mockData.asianHornetInfo.identification.map((trait, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-gray-700">{trait}</p>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="nests" className="border border-gray-200 rounded-lg overflow-hidden">
              <AccordionTrigger className="hover:no-underline px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex items-center space-x-3">
                  <Home className="h-5 w-5 text-yellow-600" />
                  <span className="text-lg font-semibold">Types de nids</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 py-4">
                <div className="space-y-4">
                  {mockData.asianHornetInfo.nestTypes.map((type, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-gray-700">{type}</p>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="behavior" className="border border-gray-200 rounded-lg overflow-hidden">
              <AccordionTrigger className="hover:no-underline px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex items-center space-x-3">
                  <Target className="h-5 w-5 text-yellow-600" />
                  <span className="text-lg font-semibold">Comportement et cycle de vie</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 py-4">
                <div className="space-y-4">
                  <p className="text-gray-700">
                    <strong>Printemps :</strong> Les reines fondatrices sortent d'hibernation et construisent de petits nids primaires.
                  </p>
                  <p className="text-gray-700">
                    <strong>Été :</strong> Construction de nids secondaires plus grands, souvent en hauteur. Période d'activité intense.
                  </p>
                  <p className="text-gray-700">
                    <strong>Automne :</strong> Reproduction et préparation à l'hibernation. Les colonies peuvent compter jusqu'à 2000 individus.
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="diet" className="border border-gray-200 rounded-lg overflow-hidden">
              <AccordionTrigger className="hover:no-underline px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex items-center space-x-3">
                  <Utensils className="h-5 w-5 text-yellow-600" />
                  <span className="text-lg font-semibold">Alimentation</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 py-4">
                <div className="space-y-4">
                  <p className="text-gray-700">
                    <strong>Prédateur redoutable :</strong> Le frelon asiatique se nourrit principalement d'abeilles domestiques, 
                    mais aussi de guêpes, mouches et autres insectes pollinisateurs.
                  </p>
                  <p className="text-gray-700">
                    <strong>Impact sur les ruches :</strong> Un seul frelon peut tuer jusqu'à 50 abeilles par jour. 
                    Une colonie peut détruire une ruche entière en quelques semaines.
                  </p>
                  <p className="text-gray-700">
                    <strong>Fruits et nectar :</strong> Les adultes se nourrissent également de fruits mûrs et de nectar, 
                    causant des dégâts dans les vergers.
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Emergency CTA */}
      <section className="py-20 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-8">
            <div className="flex justify-center">
              <AlertTriangle className="h-16 w-16 text-yellow-400" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold">
              Vous avez repéré un frelon asiatique ?
            </h2>
            <p className="text-xl leading-relaxed max-w-3xl mx-auto">
              N'intervenez jamais vous-même ! Le frelon asiatique peut être très agressif et attaquer en groupe. 
              Contactez immédiatement notre équipe d'experts pour une intervention sécurisée.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={handleCall}
                className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-8 py-4 text-xl rounded-lg transition-all duration-200 transform hover:scale-105"
              >
                <Phone className="h-6 w-6 mr-2" />
                Urgence : 06 76 58 30 38
              </Button>
              <Link to="/contact">
                <Button
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-red-600 font-semibold px-8 py-4 text-xl rounded-lg transition-all duration-200 w-full sm:w-auto"
                >
                  Demander un devis gratuit
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AsianHornet;